<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { generateSampleData } from '../helpers/sampleDataGenerator';
import ReportChart from '../../../components/ReportChart.vue';
import {
  buildChartData,
  buildChartProps,
  hasChartData,
  formatMetricValue,
  formatTableCell,
  buildTableCellTotals,
  formatPeriodLabel,
} from '../../../helpers/reportChartHelpers';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const metrics = computed(() => props.config?.metrics || []);
const dimensions = computed(() => props.config?.dimensions || []);
const defaultVisualization = computed(
  () => props.config?.default_visualization || 'bar_chart'
);
const granularity = computed(
  () => props.config?.default_time_granularity || 'day'
);
const valueMode = computed(
  () => props.config?.default_value_mode || 'absolute'
);
const showSummaryCards = computed(
  () => props.config?.show_summary_cards ?? false
);

const isTable = computed(() => defaultVisualization.value === 'table');
const isNoTimeAggregation = computed(() => granularity.value === 'none');

// The viewer opens grouped by default_group_by (or ungrouped when unset), so the
// preview must sample the same way — not blindly by the first configured dimension.
const previewDimensions = computed(() => {
  const groupBy = props.config?.default_group_by;
  if (!groupBy) return [];
  const configured = dimensions.value.find(
    dim => (dim.property || dim.source) === groupBy
  );
  // A built-in dimension (agent/inbox/...) has no entry in `dimensions`.
  return [configured || { source: groupBy }];
});

const sampleData = computed(() => {
  if (!metrics.value.length) return null;
  return generateSampleData(
    metrics.value,
    previewDimensions.value,
    granularity.value
  );
});

const hasDimension = computed(() => !!sampleData.value?.hasDimension);

// Column totals for rendering table cells as shares in percentage value mode.
const tableCellTotal = computed(() =>
  buildTableCellTotals(
    sampleData.value?.timeseries,
    metrics.value,
    hasDimension.value && !isNoTimeAggregation.value
  )
);

const chartData = computed(() => {
  if (!sampleData.value) return null;
  return buildChartData({
    timeseries: sampleData.value.timeseries,
    metrics: metrics.value,
    granularity: granularity.value,
    hasDimension: hasDimension.value,
    visualization: defaultVisualization.value,
    valueMode: valueMode.value,
  });
});

const chartProps = computed(() =>
  buildChartProps({
    valueMode: valueMode.value,
    hasDimension: hasDimension.value,
    granularity: granularity.value,
    metrics: metrics.value,
  })
);

const hasChart = computed(() => hasChartData(chartData.value));

const tableColumns = computed(() => {
  const cols = [];
  if (!isNoTimeAggregation.value) {
    cols.push({
      key: '__period',
      label: t('WGPT_CUSTOM_REPORTS.VIEWER.PERIOD'),
    });
  }
  if (hasDimension.value) {
    cols.push({
      key: '__dimension',
      label: sampleData.value?.dimensionLabel || '',
    });
  }
  metrics.value.forEach(metric => {
    cols.push({ key: metric.key, label: metric.label || metric.key });
  });
  return cols;
});

function metricLabel(metric) {
  return metric.label || metric.key || '—';
}
</script>

<template>
  <div class="pt-6 border-t border-n-weak">
    <div class="flex items-center gap-2 mb-3">
      <span class="i-lucide-eye text-n-slate-9 w-4 h-4" />
      <span class="text-xs font-medium text-n-slate-9 uppercase tracking-wide">
        {{ t('WGPT_CUSTOM_REPORTS.PREVIEW.BADGE') }}
      </span>
    </div>

    <div
      v-if="showSummaryCards && sampleData && metrics.length"
      class="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4"
    >
      <div
        v-for="metric in metrics"
        :key="metric.key"
        class="bg-n-surface-2 rounded-lg p-3"
      >
        <p class="text-xs text-n-slate-11 mb-0.5 truncate">
          {{ metricLabel(metric) }}
        </p>
        <p class="text-lg font-semibold text-n-slate-12">
          {{ formatMetricValue(sampleData.summary[metric.key]) }}
        </p>
      </div>
    </div>

    <div
      v-if="isTable && sampleData"
      class="overflow-x-auto bg-n-surface-2 rounded-lg p-4"
    >
      <table class="w-full">
        <thead>
          <tr class="border-b border-n-strong">
            <th
              v-for="col in tableColumns"
              :key="col.key"
              class="py-2 px-3 text-xs font-medium text-n-slate-11"
              :class="col.key.startsWith('__') ? 'text-left' : 'text-right'"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in sampleData.timeseries"
            :key="idx"
            class="border-b border-n-weak"
          >
            <td
              v-for="col in tableColumns"
              :key="col.key"
              class="py-2 px-3 text-sm text-n-slate-12"
              :class="col.key.startsWith('__') ? 'text-left' : 'text-right'"
            >
              <template v-if="col.key === '__period'">
                {{ formatPeriodLabel(row.period, granularity) }}
              </template>
              <template v-else-if="col.key === '__dimension'">
                {{ row.dimension_value || '—' }}
              </template>
              <template v-else>
                {{
                  formatTableCell(
                    row[col.key],
                    valueMode,
                    tableCellTotal(row, col.key)
                  )
                }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="hasChart" class="bg-n-surface-2 rounded-lg p-4">
      <ReportChart
        :visualization="defaultVisualization"
        :data="chartData"
        :aria-label="t('WGPT_CUSTOM_REPORTS.PREVIEW.BADGE')"
        :height="208"
        :timeseries="!isNoTimeAggregation"
        v-bind="chartProps"
      />
    </div>

    <div
      v-else
      class="bg-n-surface-2 rounded-lg p-8 flex flex-col items-center justify-center text-center"
    >
      <span class="i-lucide-bar-chart-3 text-n-slate-9 w-8 h-8 mb-2" />
      <p class="text-sm text-n-slate-11">
        {{ t('WGPT_CUSTOM_REPORTS.PREVIEW.EMPTY') }}
      </p>
    </div>

    <div v-if="dimensions.length" class="flex flex-wrap gap-2 mt-3">
      <span class="text-xs text-n-slate-11">
        {{ t('WGPT_CUSTOM_REPORTS.PREVIEW.DIMENSIONS_LABEL') }}
      </span>
      <span
        v-for="dim in dimensions"
        :key="dim.property || dim.source"
        class="inline-flex items-center px-2 py-0.5 rounded-full bg-n-alpha-2 text-xs text-n-slate-11"
      >
        {{ dim.label || dim.property || dim.source }}
      </span>
    </div>
  </div>
</template>
